/**
 * The punch endpoints, injected into the one base slice (D-17).
 *
 * Everything here is a `POST` except the context read, and **no punch response
 * is ever cached**: the service worker treats `/api/v1/punches/*` as
 * `NetworkOnly`, and on this side every mutation result is `reset()` by the
 * thunk that started it (see `thunks.ts`) so nothing lingers in the store. A
 * cached punch response is a fabricated punch.
 *
 * `Timesheet` is on every punch mutation for the same reason `Shift` is:
 * `hoursApi` provides `{ type: 'Timesheet', id: 'MY_HOURS' }`, and a worker who
 * punches out at 06:00 and taps « Mes heures » must not be served the cached
 * page from before their punch — they would read it as a punch that was lost.
 */
import { baseApi, zodResponse } from '@saniplus/api-client';
import {
  MeContextResponseSchema,
  PunchResultResponseSchema,
  PunchSyncResponseSchema,
  type MeContextResponse,
  type PunchInRequest,
  type PunchOutRequest,
  type PunchResultResponse,
  type PunchSyncRequest,
  type PunchSyncResponse,
} from '@saniplus/contracts';

export const punchApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * The single source of truth for the home screen. Cached for the tab's
     * life only; the durable copy is the IndexedDB `contextCache`, because
     * RTK Query's cache does not survive the cold start iOS does routinely.
     */
    getMeContext: build.query<MeContextResponse, void>({
      query: () => ({ url: '/me/context' }),
      ...zodResponse(MeContextResponseSchema),
      providesTags: ['Context'],
    }),

    punchIn: build.mutation<PunchResultResponse, PunchInRequest>({
      query: (body) => ({ url: '/punches/in', method: 'POST', body }),
      ...zodResponse(PunchResultResponseSchema),
      invalidatesTags: ['Context', 'Session', 'Punch', 'Shift', 'Timesheet'],
    }),

    punchOut: build.mutation<PunchResultResponse, PunchOutRequest>({
      query: (body) => ({ url: '/punches/out', method: 'POST', body }),
      ...zodResponse(PunchResultResponseSchema),
      invalidatesTags: ['Context', 'Session', 'Punch', 'Shift', 'Timesheet'],
    }),

    /** The batch. Per-item results; partial failures are visible, never lost. */
    syncPunches: build.mutation<PunchSyncResponse, PunchSyncRequest>({
      query: (body) => ({ url: '/punches/sync', method: 'POST', body }),
      ...zodResponse(PunchSyncResponseSchema),
      invalidatesTags: ['Context', 'Session', 'Punch', 'Shift', 'Timesheet'],
    }),
  }),
});

export const {
  useGetMeContextQuery,
  usePunchInMutation,
  usePunchOutMutation,
  useSyncPunchesMutation,
} = punchApi;
